import { useState, useEffect, useMemo, Dispatch, SetStateAction } from "react";
import { Input } from "@/components/ui/input";
import { Check, Search, BookOpen, ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import { IntakeTerm, Degree, HubProgram } from "@/services/universityService";
import ProgramBasketWidget from "@/components/ProgramBasketWidget";
import { formatDuration } from "@/utils/helpers";

interface ProgramSelectionProps {
    selectedTerm: string | null;
    setSelectedTerm: (term: string | null) => void;
    selectedDegree: string | null;
    setSelectedDegree: (degree: string | null) => void;
    /** Ordered list of selected program IDs (first = primary/first choice) */
    selectedPrograms: number[];
    setSelectedPrograms: Dispatch<SetStateAction<number[]>>;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    programs: HubProgram[];
    degrees: Degree[];
    intakeTerms: IntakeTerm[];
    programsPerPage: number;
    loadingTerms: boolean;
    loadingDegrees: boolean;
    loadingPrograms: boolean;
    /** Maximum number of PLEs the user may select (from degree.max_ple_for_same_institution, null → 3) */
    maxSelectable: number;
    /** Called when "Apply Now" is clicked in the basket widget — advances the wizard. */
    onApplyNow?: () => void;
}

function formatFee(fee: number, currencyCode: string = "USD"): string {
    return fee.toLocaleString("en-US", { style: "currency", currency: currencyCode, maximumFractionDigits: 0 });
}

const CURRENCY_SYMBOLS: Record<string, string> = {
    USD: "$",
    EUR: "€",
    TRY: "₺",
    GBP: "£",
    JPY: "¥",
    CNY: "¥",
    CAD: "CA$",
    AUD: "A$",
    CHF: "Fr",
    KRW: "₩",
    INR: "₹",
    BRL: "R$",
    MXN: "MX$",
    SAR: "﷼",
    AED: "د.إ",
};

function getCurrencySymbol(currencyCode: string): string {
    return CURRENCY_SYMBOLS[currencyCode.toUpperCase()] ?? "";
}

export default function ProgramSelection({
    selectedTerm,
    setSelectedTerm,
    selectedDegree,
    setSelectedDegree,
    selectedPrograms,
    setSelectedPrograms,
    searchQuery,
    setSearchQuery,
    programs = [],
    degrees = [],
    intakeTerms = [],
    programsPerPage,
    loadingTerms,
    loadingDegrees,
    loadingPrograms,
    maxSelectable,
    onApplyNow,
}: ProgramSelectionProps) {
    const [currentPage, setCurrentPage] = useState(1);
    const [filterLanguage, setFilterLanguage] = useState<string>("");
    const [filterThesis, setFilterThesis] = useState<string>("");
    const [filterFaculty, setFilterFaculty] = useState<string>("");
    const [filterFeeMin, setFilterFeeMin] = useState<string>("");
    const [filterFeeMax, setFilterFeeMax] = useState<string>("");
    const [filterDuration, setFilterDuration] = useState<string>("");

    // Currency code taken from the first program that has one, matching HomePage behaviour
    const currencyCode = programs.find((prog) => prog.currency_code)?.currency_code || "USD";

    // Derive unique filter options from loaded programs
    const languages = useMemo(() =>
        Array.from(new Set(programs.map((prog) => prog.language).filter(Boolean))).sort() as string[]
    , [programs]);

    const thesisOptions = useMemo(() =>
        Array.from(new Set(programs.map((prog) => prog.thesis_requirement ?? "").filter(Boolean))).sort() as string[]
    , [programs]);

    const faculties = useMemo(() =>
        Array.from(new Set(programs.map((prog) => prog.faculty ?? "").filter(Boolean))).sort() as string[]
    , [programs]);

    const durationOptions = useMemo(() =>
        Array.from(new Set(programs.map((prog) => prog.duration ?? "").filter(Boolean))).sort() as string[]
    , [programs]);

    const parsedFeeMin = filterFeeMin !== "" ? parseFloat(filterFeeMin) : null;
    const parsedFeeMax = filterFeeMax !== "" ? parseFloat(filterFeeMax) : null;
    const feeInvertedRange = parsedFeeMin !== null && parsedFeeMax !== null && parsedFeeMin > parsedFeeMax;

    const hasActiveFilters = !!(filterLanguage || filterThesis || filterFaculty || filterFeeMin || filterFeeMax || filterDuration);

    // Reset to page 1 whenever filters or programs change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, selectedDegree, programs, filterLanguage, filterThesis, filterFaculty, filterFeeMin, filterFeeMax, filterDuration]);

    // Reset filters when programs list changes (new term/degree selected)
    useEffect(() => {
        setFilterLanguage("");
        setFilterThesis("");
        setFilterFaculty("");
        setFilterFeeMin("");
        setFilterFeeMax("");
        setFilterDuration("");
    }, [programs]);

    const filteredPrograms = useMemo(() => {
        return programs
            .filter((prog) => (prog.name ?? "").toLowerCase().includes(searchQuery.toLowerCase()))
            .filter((prog) => !selectedDegree || prog.degree_type === selectedDegree)
            .filter((prog) => !filterLanguage || prog.language === filterLanguage)
            .filter((prog) => !filterThesis || (prog.thesis_requirement ?? "") === filterThesis)
            .filter((prog) => !filterFaculty || (prog.faculty ?? "") === filterFaculty)
            .filter((prog) => !filterDuration || (prog.duration ?? "") === filterDuration)
            .filter((prog) => {
                const hasBound = parsedFeeMin !== null || parsedFeeMax !== null;
                if (!hasBound || feeInvertedRange) return true;
                const effectiveFee = prog.discounted_tuition_fee ?? prog.tuition_fee;
                if (effectiveFee == null) return false;
                if (parsedFeeMin !== null && effectiveFee < parsedFeeMin) return false;
                if (parsedFeeMax !== null && effectiveFee > parsedFeeMax) return false;
                return true;
            });
    }, [programs, searchQuery, selectedDegree, filterLanguage, filterThesis, filterFaculty, filterDuration, parsedFeeMin, parsedFeeMax, feeInvertedRange]);

    const totalPages = Math.ceil(filteredPrograms.length / programsPerPage);
    const safePage = Math.min(currentPage, Math.max(totalPages, 1));
    const pageStart = (safePage - 1) * programsPerPage;
    const pagePrograms = filteredPrograms.slice(pageStart, pageStart + programsPerPage);

    return (
        <>
        <div className="space-y-8 md:space-y-16">
            <div>
                <h2 className="text-2xl md:text-4xl font-serif font-black text-[#001B44] leading-tight">
                    Program Selection
                </h2>
                <p className="text-slate-900 mt-2 md:mt-4 text-base md:text-lg font-medium max-w-2xl">
                    Begin your application by selecting your preferred term and program of study.
                </p>
            </div>

            <div className="space-y-8 md:space-y-14">
                {/* 1. Intake Term */}
                <section className="space-y-6">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[#001B44]">
                            1
                        </span>
                        Intake Term
                    </h4>

                    {loadingTerms ? (
                        <div className="flex items-center gap-3 py-8 text-slate-400">
                            <Loader2 className="h-5 w-5 animate-spin shrink-0" />
                            <span className="text-xs font-bold uppercase tracking-widest">Loading terms…</span>
                        </div>
                    ) : intakeTerms.length === 0 ? (
                        <div className="py-8 px-6 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 text-center">
                            <p className="text-slate-500 font-medium">No intake terms are currently available.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {intakeTerms.map((term) => (
                                <button
                                    key={term.id}
                                    onClick={() => setSelectedTerm(term.id)}
                                    className={`p-6 rounded-2xl border-2 transition-all text-left group min-w-0 ${
                                        selectedTerm === term.id
                                            ? "border-[#001B44] bg-[#001B44]/[0.02] shadow-sm"
                                            : "border-slate-100 bg-white hover:border-slate-200"
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-3 min-w-0">
                                        <p className={`font-black text-base truncate min-w-0 ${selectedTerm === term.id ? "text-[#001B44]" : "text-slate-700"}`}>
                                            {term.name}
                                        </p>
                                        <div className={`w-6 h-6 rounded-full border-2 transition-all flex items-center justify-center shrink-0 ${
                                            selectedTerm === term.id
                                                ? "border-emerald-500 bg-emerald-500"
                                                : "border-slate-200"
                                        }`}>
                                            {selectedTerm === term.id && (
                                                <Check className="h-3 w-3 text-white stroke-[3]" />
                                            )}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </section>

                {/* 2. Degree Level */}
                <section className={`space-y-6 transition-all duration-500 ${selectedTerm ? "opacity-100" : "opacity-30 pointer-events-none grayscale"}`}>
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[#001B44]">
                            2
                        </span>
                        Degree Level
                    </h4>
                    {loadingDegrees ? (
                        <div className="flex items-center gap-3 py-8 text-slate-400">
                            <Loader2 className="h-5 w-5 animate-spin shrink-0" />
                            <span className="text-xs font-bold uppercase tracking-widest">Loading degrees…</span>
                        </div>
                    ) : !selectedTerm ? (
                        <div className="py-8 px-6 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 text-center">
                            <p className="text-slate-500 font-medium">Select a term to see available degrees.</p>
                        </div>
                    ) : degrees.length === 0 ? (
                        <div className="py-8 px-6 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 text-center">
                            <p className="text-slate-500 font-medium">No degree options available for this term.</p>
                        </div>
                    ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-5">
                        {degrees.map((degree) => (
                            <button
                                key={degree.id}
                                onClick={() => setSelectedDegree(degree.degree)}
                                className={`p-3 md:p-6 rounded-xl md:rounded-2xl border-2 transition-all text-center min-w-0 ${
                                    selectedDegree === degree.degree
                                        ? "border-[#001B44] bg-[#001B44]/[0.02] shadow-sm"
                                        : "border-slate-100 bg-white hover:border-slate-200"
                                }`}
                            >
                                <p className={`font-black text-[10px] sm:text-xs uppercase tracking-[0.1em] sm:tracking-[0.2em] break-words ${selectedDegree === degree.degree ? "text-[#001B44]" : "text-slate-900"}`}>
                                    {degree.degree}
                                </p>
                            </button>
                        ))}
                    </div>
                    )}
                </section>

                {/* 3. Program List */}
                <section className={`space-y-6 transition-all duration-500 ${selectedDegree ? "opacity-100" : "opacity-30 pointer-events-none grayscale"}`}>
                    {/* Header row: label + search */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3 shrink-0">
                            <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[#001B44]">
                                3
                            </span>
                            Select Program
                        </h4>
                        <div className="relative w-full md:w-80">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-900" />
                            <Input
                                placeholder="Search by program name…"
                                className="pl-12 h-12 rounded-2xl border-slate-200 bg-slate-50 focus:bg-white text-xs font-bold transition-all"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Filters */}
                    {programs.length > 0 && (
                        <div className="flex flex-wrap gap-2 items-start">
                            {/* Language filter */}
                            {languages.length > 1 && (
                                <select
                                    value={filterLanguage}
                                    onChange={(e) => setFilterLanguage(e.target.value)}
                                    className={`h-9 px-3 pr-8 rounded-xl border-2 text-[11px] font-bold uppercase tracking-wide bg-white transition-all appearance-none cursor-pointer ${
                                        filterLanguage ? "border-[#001B44] text-[#001B44]" : "border-slate-100 text-slate-500 hover:border-slate-200"
                                    }`}
                                >
                                    <option value="">All Languages</option>
                                    {languages.map((lang) => (
                                        <option key={lang} value={lang}>{lang}</option>
                                    ))}
                                </select>
                            )}

                            {/* Faculty filter */}
                            {faculties.length > 1 && (
                                <select
                                    value={filterFaculty}
                                    onChange={(e) => setFilterFaculty(e.target.value)}
                                    className={`h-9 px-3 pr-8 rounded-xl border-2 text-[11px] font-bold uppercase tracking-wide bg-white transition-all appearance-none cursor-pointer ${
                                        filterFaculty ? "border-[#001B44] text-[#001B44]" : "border-slate-100 text-slate-500 hover:border-slate-200"
                                    }`}
                                >
                                    <option value="">All Faculties</option>
                                    {faculties.map((fac) => (
                                        <option key={fac} value={fac}>{fac}</option>
                                    ))}
                                </select>
                            )}

                            {/* Thesis filter */}
                            {thesisOptions.length > 0 && (
                                <select
                                    value={filterThesis}
                                    onChange={(e) => setFilterThesis(e.target.value)}
                                    className={`h-9 px-3 pr-8 rounded-xl border-2 text-[11px] font-bold uppercase tracking-wide bg-white transition-all appearance-none cursor-pointer ${
                                        filterThesis ? "border-[#001B44] text-[#001B44]" : "border-slate-100 text-slate-500 hover:border-slate-200"
                                    }`}
                                >
                                    <option value="">Any Thesis</option>
                                    {thesisOptions.map((opt) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            )}

                            {/* Duration filter */}
                            {durationOptions.length > 1 && (
                                <select
                                    value={filterDuration}
                                    onChange={(e) => setFilterDuration(e.target.value)}
                                    className={`h-9 px-3 pr-8 rounded-xl border-2 text-[11px] font-bold uppercase tracking-wide bg-white transition-all appearance-none cursor-pointer ${
                                        filterDuration ? "border-[#001B44] text-[#001B44]" : "border-slate-100 text-slate-500 hover:border-slate-200"
                                    }`}
                                >
                                    <option value="">All Durations</option>
                                    {durationOptions.map((dur) => (
                                        <option key={dur} value={dur}>{formatDuration(dur)}</option>
                                    ))}
                                </select>
                            )}

                            {/* Tuition fee range filter */}
                            {programs.some((prog) => prog.tuition_fee != null) && (
                                <div className="flex flex-col gap-1">
                                    <div className="flex gap-2">
                                        <div className="relative">
                                            {getCurrencySymbol(currencyCode) && (
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px] font-bold text-slate-400">{getCurrencySymbol(currencyCode)}</span>
                                            )}
                                            <Input
                                                type="number"
                                                placeholder="Min fee"
                                                value={filterFeeMin}
                                                onChange={(e) => setFilterFeeMin(e.target.value)}
                                                className={`h-9 ${getCurrencySymbol(currencyCode) ? "pl-6" : "pl-3"} pr-3 w-32 rounded-xl border-2 text-[11px] font-bold transition-all ${
                                                    filterFeeMin ? "border-[#001B44] text-[#001B44]" : "border-slate-100 text-slate-500"
                                                }`}
                                            />
                                        </div>
                                        <div className="relative">
                                            {getCurrencySymbol(currencyCode) && (
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px] font-bold text-slate-400">{getCurrencySymbol(currencyCode)}</span>
                                            )}
                                            <Input
                                                type="number"
                                                placeholder="Max fee"
                                                value={filterFeeMax}
                                                onChange={(e) => setFilterFeeMax(e.target.value)}
                                                className={`h-9 ${getCurrencySymbol(currencyCode) ? "pl-6" : "pl-3"} pr-3 w-32 rounded-xl border-2 text-[11px] font-bold transition-all ${
                                                    filterFeeMax ? "border-[#001B44] text-[#001B44]" : "border-slate-100 text-slate-500"
                                                }`}
                                            />
                                        </div>
                                    </div>
                                    {feeInvertedRange && (
                                        <p className="text-[10px] font-bold text-amber-600">Min must be ≤ max.</p>
                                    )}
                                </div>
                            )}

                            {/* Clear filters */}
                            {hasActiveFilters && (
                                <button
                                    onClick={() => { setFilterLanguage(""); setFilterThesis(""); setFilterFaculty(""); setFilterFeeMin(""); setFilterFeeMax(""); setFilterDuration(""); }}
                                    className="h-9 px-3 flex items-center gap-1.5 rounded-xl border-2 border-slate-100 text-[11px] font-bold uppercase tracking-wide text-slate-400 hover:border-red-200 hover:text-red-500 transition-all"
                                >
                                    <X className="h-3 w-3" />
                                    Clear
                                </button>
                            )}
                        </div>
                    )}

                    {/* Program grid / loading / empty state */}
                    {loadingPrograms ? (
                        <div className="flex items-center gap-3 py-12 text-slate-400">
                            <Loader2 className="h-5 w-5 animate-spin shrink-0" />
                            <span className="text-xs font-bold uppercase tracking-widest">Loading programs…</span>
                        </div>
                    ) : filteredPrograms.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 px-6 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                                <BookOpen className="h-8 w-8 text-slate-400" />
                            </div>
                            <h4 className="text-lg font-black text-slate-900 mb-2">No Programs Available</h4>
                            <p className="text-sm text-slate-600 text-center max-w-md">
                                {searchQuery || hasActiveFilters
                                    ? "No programs match your filters. Try adjusting your search or clearing filters."
                                    : `No ${selectedDegree || ""} programs are currently available for the selected term.`
                                }
                            </p>
                        </div>
                    ) : (
                        <>
                            {maxSelectable > 1 && (
                                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                                    Select up to <span className="text-[#001B44]">{maxSelectable}</span> programs in order of preference.{" "}
                                    <span className="text-emerald-600">{selectedPrograms.length} selected.</span>
                                </p>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {pagePrograms.map((program) => {
                                    const selectionIndex = selectedPrograms.indexOf(program.id);
                                    const isSelected = selectionIndex !== -1;
                                    const hasDiscount = program.discounted_tuition_fee != null && program.tuition_fee != null && program.discounted_tuition_fee < program.tuition_fee;
                                    const displayFee = program.discounted_tuition_fee ?? program.tuition_fee;

                                    const handleToggle = () => {
                                        if (isSelected) {
                                            // Deselect: remove from list
                                            setSelectedPrograms(selectedPrograms.filter((id) => id !== program.id));
                                        } else {
                                            // Select: append if under cap
                                            if (selectedPrograms.length < maxSelectable) {
                                                setSelectedPrograms([...selectedPrograms, program.id]);
                                            }
                                        }
                                    };

                                    return (
                                        <button
                                            key={program.id}
                                            onClick={handleToggle}
                                            disabled={!isSelected && selectedPrograms.length >= maxSelectable}
                                            className={`p-5 rounded-2xl border-2 transition-all text-left flex flex-col gap-4 min-w-0 ${
                                                isSelected
                                                    ? "border-[#001B44] bg-[#001B44]/[0.02] shadow-sm"
                                                    : selectedPrograms.length >= maxSelectable
                                                        ? "border-slate-100 bg-slate-50 opacity-40 cursor-not-allowed"
                                                        : "border-slate-100 bg-white hover:border-slate-200"
                                            }`}
                                        >
                                            {/* Top row: icon + name/faculty + order badge */}
                                            <div className="flex items-start gap-4 min-w-0">
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-colors ${
                                                    isSelected
                                                        ? "bg-emerald-500 text-white border-emerald-500"
                                                        : "bg-slate-50 text-slate-900 border-slate-100"
                                                }`}>
                                                    {isSelected
                                                        ? maxSelectable > 1
                                                            ? <span className="text-sm font-black text-white">{selectionIndex + 1}</span>
                                                            : <Check className="h-5 w-5" />
                                                        : <BookOpen className="h-5 w-5" />
                                                    }
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className={`font-black text-sm truncate ${isSelected ? "text-[#001B44]" : "text-slate-800"}`}>
                                                        {program.name}
                                                    </h4>
                                                    {program.faculty && (
                                                        <p className="text-[10px] text-slate-500 font-bold uppercase truncate tracking-wider mt-0.5">
                                                            {program.faculty}
                                                        </p>
                                                    )}
                                                </div>

                                            </div>

                                            {/* Pills */}
                                            <div className="flex flex-wrap gap-1.5">
                                                {program.discipline && (
                                                    <span className="px-2 py-0.5 rounded-lg bg-slate-100 text-[10px] font-bold text-slate-600 uppercase tracking-wide truncate max-w-[140px]">
                                                        {program.discipline}
                                                    </span>
                                                )}
                                                {program.language && (
                                                    <span className="px-2 py-0.5 rounded-lg bg-blue-50 text-[10px] font-bold text-blue-600 uppercase tracking-wide truncate max-w-[120px]">
                                                        {program.language}
                                                    </span>
                                                )}
                                                {program.mode_of_study && (
                                                    <span className="px-2 py-0.5 rounded-lg bg-violet-50 text-[10px] font-bold text-violet-600 uppercase tracking-wide truncate max-w-[120px]">
                                                        {program.mode_of_study}
                                                    </span>
                                                )}
                                                {program.thesis_requirement && (
                                                    <span className="px-2 py-0.5 rounded-lg bg-amber-50 text-[10px] font-bold text-amber-700 uppercase tracking-wide truncate max-w-[120px]">
                                                        {program.thesis_requirement}
                                                    </span>
                                                )}
                                                {program.duration && (
                                                    <span className="px-2 py-0.5 rounded-lg bg-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-wide truncate max-w-[120px]">
                                                        {formatDuration(program.duration)}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Tuition fee */}
                                            {displayFee != null && (
                                                <div className="flex items-baseline gap-2 pt-1 border-t border-slate-100">
                                                    <span className={`text-sm font-black ${isSelected ? "text-[#001B44]" : "text-slate-800"}`}>
                                                        {formatFee(displayFee, currencyCode)}
                                                    </span>
                                                    {hasDiscount && (
                                                        <span className="text-[11px] font-bold text-slate-400 line-through">
                                                            {formatFee(program.tuition_fee!, currencyCode)}
                                                        </span>
                                                    )}
                                                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">/ year</span>
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            {totalPages > 1 && (
                                <div className="flex items-center justify-between pt-2">
                                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                                        {pageStart + 1}–{Math.min(pageStart + programsPerPage, filteredPrograms.length)} of {filteredPrograms.length}
                                    </p>
                                    <div className="flex items-center gap-1.5">
                                        <button
                                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                            disabled={safePage === 1}
                                            className="w-9 h-9 rounded-xl border-2 border-slate-100 bg-white flex items-center justify-center text-slate-400 hover:border-slate-200 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </button>
                                        {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                className={`w-9 h-9 rounded-xl border-2 text-xs font-black transition-all ${
                                                    page === safePage
                                                        ? "border-[#001B44] bg-[#001B44] text-white"
                                                        : "border-slate-100 bg-white text-slate-400 hover:border-slate-200 hover:text-slate-700"
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                        <button
                                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                            disabled={safePage === totalPages}
                                            className="w-9 h-9 rounded-xl border-2 border-slate-100 bg-white flex items-center justify-center text-slate-400 hover:border-slate-200 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </section>
            </div>
        </div>

        {/* Floating basket widget — shows the ordered selection and an Apply Now shortcut */}
        {onApplyNow && (
            <ProgramBasketWidget
                selectedProgramIds={selectedPrograms}
                programs={programs}
                maxSelectable={maxSelectable}
                onRemove={(programId) => setSelectedPrograms((prev) => prev.filter((id) => id !== programId))}
                onReorder={setSelectedPrograms}
                onApplyNow={onApplyNow}
            />
        )}
        </>
    );
}
